import { Button } from '@/components/ui';
import TooltipWrapper from '@/components/ui/tooltip';
import { useFetchAccount } from '@/features/account/api/get-account';
import useAvailableFunds from '@/features/account/hooks/use-available-funds';
import { getBboPrice, getOrderValue } from '@/features/trade/order/utils/math';
import { getIsStatusEval } from '@/features/vest-capital/utils/status';
import useFundedAccount, { isMarketAllowed } from '@/hooks/use-funded-account';
import useLeverage from '@/hooks/use-leverage';
import {
  getMigrationBlockButtonKey,
  getMigrationBlockTooltipKey,
  useMigrationTradingBlock,
} from '@/hooks/use-migration';
import useSymbol from '@/hooks/use-symbol';
import { useWeekendTrading } from '@/hooks/use-weekend-trading';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { useMarketStore } from '@/store/use-markets-store';
import { CapitalStatus, MarketStatus, OrderType, TimeInForce } from '@/types';
import {
  bigIntToDecimalStr,
  bigIntToNum,
  parseDecimalToBigInt,
} from '@/utils/value-format';
import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useStore } from 'zustand';
import { useShallow } from 'zustand/react/shallow';

import { OrderFormSchema } from '../order-form';

type Props = {
  tpslChecked: boolean;
  scaleOrders: OrderFormSchema[];
};

const MIN_SIZE_USD: number = Number(
  import.meta.env.VITE_MIN_ORDER_SIZE_USD || 0,
);

export default function SubmitButton({ tpslChecked, scaleOrders }: Props) {
  const { t } = useTranslation();
  const symbol = useSymbol();
  const form = useFormContext<OrderFormSchema>();

  const { isFundedOrEval } = useFundedAccount();
  const { isBlocked: isWeekendBlocked, reopenLabel } =
    useWeekendTrading(symbol);
  const { isBlocked: isMigrationBlocked, reason: migrationReason } =
    useMigrationTradingBlock();
  const { isPending: isFetchAccountPending } = useFetchAccount(); // might need to clear cache on disconnect
  const isBuy = form.watch('order.isBuy');

  const { bboPrice, midPrice, bestAsk, bestBid, sizeDecimals } = useStore(
    useMarketStore(symbol),
    useShallow((state) => ({
      bboPrice: getBboPrice(symbol, isBuy),
      midPrice: state.marketData.midPrice,
      bestAsk: state.book.asks.at(0)?.at(0),
      bestBid: state.book.bids.at(0)?.at(0),
      sizeDecimals: state.marketSpec.sizeDecimals,
    })),
  );

  const { hasAuth, capitalStatus } = useAccountStore(
    useShallow((state) => ({
      hasAuth: state.hasAuth,
      capitalStatus: state.getCapitalStatus(state.address),
    })),
  );

  const currentPosition = useCurrentAccount((state) => {
    const position = state.positions[symbol];
    if (!position) return;
    return {
      size: position.size,
      isLong: position.isLong,
    };
  });

  const isEval = getIsStatusEval(capitalStatus);
  const reduceOnly = form.watch('order.reduceOnly');
  const orderType = form.watch('order.orderType');
  const limitPrice = form.watch('order.limitPrice');
  const timeInForce = form.watch('order.timeInForce');
  const size = parseDecimalToBigInt(form.watch('order.size'), sizeDecimals);

  const startPrice = form.watch('startPrice');
  const endPrice = form.watch('endPrice');
  const ordersCount = form.watch('ordersCount');
  const sizeSkew = form.watch('sizeSkew');

  const tpPrice = form.watch('tp.limitPrice');
  const slPrice = form.watch('sl.limitPrice');

  const leverage = useLeverage(symbol);
  const availableFunds = useAvailableFunds(symbol, {
    leverage,
  });

  useEffect(() => {
    form.setValue(
      'order.initMarginRatio',
      bigIntToDecimalStr(10n ** 4n / BigInt(leverage), 4n),
    );
  }, [leverage]);

  const { buttonDisabled, buttonText } = useMemo(() => {
    const { marketSpec } = useMarketStore(symbol).getState();

    if (marketSpec.status === MarketStatus.HALT) {
      return { buttonDisabled: true, buttonText: t('orderForm.marketClosed') };
    }

    if (!hasAuth || isFetchAccountPending) {
      return { buttonDisabled: true };
    }

    if (isFundedOrEval) {
      if (
        capitalStatus !== CapitalStatus.ACTIVE &&
        capitalStatus !== CapitalStatus.FUNDED
      ) {
        return {
          buttonDisabled: true,
          buttonText: t('portfolio.accountNotActive'),
        };
      }

      if (!isMarketAllowed(symbol)) {
        return {
          buttonDisabled: true,
          buttonText: 'NOT ALLOWED IN EVAL',
        };
      }
    }

    if (orderType === OrderType.MARKET && isEval && !bboPrice) {
      return { buttonDisabled: true };
    }

    const price =
      orderType === OrderType.LIMIT
        ? parseDecimalToBigInt(limitPrice, marketSpec.priceDecimals)
        : isEval
          ? bboPrice!
          : midPrice;
    // No Position to Reduce
    if (!reduceOnly) {
      if (availableFunds === 0n) {
        return {
          buttonDisabled: true,
          buttonText: isFundedOrEval
            ? t('orderForm.noFundsCapital')
            : t('orderForm.depositToTrade'),
        };
      }

      const orderValue = getOrderValue(symbol, size, price)?.bigint ?? 0n;

      const enoughMargin = orderValue <= availableFunds;

      if (!enoughMargin) {
        return {
          buttonDisabled: true,
          buttonText: t('orderForm.insufficientBalance'),
        };
      }
    } else if (!currentPosition) {
      return {
        buttonDisabled: true,
        buttonText: t('orderForm.noPositionToReduce'),
      };
    } else if (currentPosition.isLong === isBuy) {
      return {
        buttonDisabled: true,
        buttonText: t('orderForm.invalidReduceOnly'),
      };
    } else if (currentPosition.size < size) {
      return { buttonDisabled: true, buttonText: t('orderForm.invalidSize') };
    }

    const maintMarginPriceDiff =
      (midPrice * marketSpec.maintMarginRatio) / 10n ** 4n;
    const maxAllowedLongPrice = midPrice + maintMarginPriceDiff;
    const minAllowedShortPrice = midPrice - maintMarginPriceDiff;

    if (orderType === OrderType.LIMIT) {
      if (timeInForce !== TimeInForce.FOK && timeInForce !== TimeInForce.IOC) {
        if (isBuy && price > maxAllowedLongPrice) {
          return {
            buttonDisabled: true,
            buttonText: t('orderForm.maxLimitPrice', {
              maxPrice: bigIntToDecimalStr(
                maxAllowedLongPrice,
                marketSpec.priceDecimals,
              ),
            }),
          };
        }

        if (!isBuy && price < minAllowedShortPrice) {
          return {
            buttonDisabled: true,
            buttonText: t('orderForm.minLimitPrice', {
              minPrice: bigIntToDecimalStr(
                minAllowedShortPrice,
                marketSpec.priceDecimals,
              ),
            }),
          };
        }
      }

      if (!isBuy && price <= midPrice / 2n) {
        return {
          buttonDisabled: true,
          buttonText: t('orderForm.minLimitPrice', {
            minPrice: bigIntToDecimalStr(
              midPrice / 2n,
              marketSpec.priceDecimals,
            ),
          }),
        };
      }

      if (price >= 10n * midPrice) {
        return {
          buttonDisabled: true,
          buttonText: t('orderForm.maxLimitPrice', {
            maxPrice: bigIntToDecimalStr(
              10n * midPrice,
              marketSpec.priceDecimals,
            ),
          }),
        };
      }

      if (price <= midPrice / 10n) {
        return {
          buttonDisabled: true,
          buttonText: t('orderForm.minLimitPrice', {
            minPrice: bigIntToDecimalStr(
              midPrice / 10n,
              marketSpec.priceDecimals,
            ),
          }),
        };
      }
    }

    // Only validate TP/SL if the checkbox is checked
    if (tpslChecked) {
      const parsedTpPrice = parseDecimalToBigInt(
        tpPrice,
        marketSpec.priceDecimals,
      );

      if (tpPrice !== '') {
        if (isBuy && parsedTpPrice <= price) {
          return {
            buttonDisabled: true,
            buttonText: t('orderForm.minTpPrice', {
              minPrice: bigIntToDecimalStr(price, marketSpec.priceDecimals),
            }),
          };
        }

        if (!isBuy && parsedTpPrice >= price) {
          return {
            buttonDisabled: true,
            buttonText: t('orderForm.maxTpPrice', {
              maxPrice: bigIntToDecimalStr(price, marketSpec.priceDecimals),
            }),
          };
        }
      }

      if (slPrice !== '') {
        const parsedSlPrice = parseDecimalToBigInt(
          slPrice,
          marketSpec.priceDecimals,
        );

        let comparePrice = price;

        switch (isBuy) {
          case true:
            if (orderType === OrderType.MARKET && bestBid) {
              comparePrice = bestBid;
            }
            if (parsedSlPrice >= comparePrice) {
              return {
                buttonDisabled: true,
                buttonText: t('orderForm.maxSlPrice', {
                  maxPrice: bigIntToDecimalStr(
                    comparePrice,
                    marketSpec.priceDecimals,
                  ),
                }),
              };
            }
            break;
          case false:
            if (orderType === OrderType.MARKET && bestAsk) {
              comparePrice = bestAsk;
            }
            if (parsedSlPrice <= comparePrice) {
              return {
                buttonDisabled: true,
                buttonText: t('orderForm.minSlPrice', {
                  minPrice: bigIntToDecimalStr(
                    comparePrice,
                    marketSpec.priceDecimals,
                  ),
                }),
              };
            }
            break;
        }
      }
    }

    if (orderType === OrderType.SCALE) {
      if (
        !(
          Number(startPrice) > 0 &&
          Number(endPrice) > 0 &&
          Number(ordersCount) > 0 &&
          Number(sizeSkew) > 0
        )
      ) {
        return {
          buttonDisabled: true,
        };
      }

      if (scaleOrders.length < 2) {
        return {
          buttonDisabled: true,
          buttonText: t('orderForm.minScaleOrdersCount', {
            min: 2,
          }),
        };
      }

      if (scaleOrders.length > 10) {
        return {
          buttonDisabled: true,
          buttonText: t('orderForm.maxScaleOrdersCount', {
            max: 10,
          }),
        };
      }

      const startPriceNum = Number(startPrice);
      const endPriceNum = Number(endPrice);
      const minPrice = parseDecimalToBigInt(
        Math.min(startPriceNum, endPriceNum).toString(),
        marketSpec.priceDecimals,
      );
      const maxPrice = parseDecimalToBigInt(
        Math.max(startPriceNum, endPriceNum).toString(),
        marketSpec.priceDecimals,
      );

      if (timeInForce !== TimeInForce.FOK && timeInForce !== TimeInForce.IOC) {
        if (isBuy && maxPrice > maxAllowedLongPrice) {
          return {
            buttonDisabled: true,
            buttonText: t('orderForm.maxLimitPrice', {
              maxPrice: bigIntToDecimalStr(
                maxAllowedLongPrice,
                marketSpec.priceDecimals,
              ),
            }),
          };
        }

        if (!isBuy && minPrice < minAllowedShortPrice) {
          return {
            buttonDisabled: true,
            buttonText: t('orderForm.minLimitPrice', {
              minPrice: bigIntToDecimalStr(
                minAllowedShortPrice,
                marketSpec.priceDecimals,
              ),
            }),
          };
        }
      }

      if (!isBuy && minPrice <= midPrice / 2n) {
        return {
          buttonDisabled: true,
          buttonText: t('orderForm.minLimitPrice', {
            minPrice: bigIntToDecimalStr(
              midPrice / 2n,
              marketSpec.priceDecimals,
            ),
          }),
        };
      }

      if (maxPrice >= 10n * midPrice) {
        return {
          buttonDisabled: true,
          buttonText: t('orderForm.maxLimitPrice', {
            maxPrice: bigIntToDecimalStr(
              10n * midPrice,
              marketSpec.priceDecimals,
            ),
          }),
        };
      }

      if (minPrice <= midPrice / 10n) {
        return {
          buttonDisabled: true,
          buttonText: t('orderForm.minLimitPrice', {
            minPrice: bigIntToDecimalStr(
              midPrice / 10n,
              marketSpec.priceDecimals,
            ),
          }),
        };
      }
    }

    let invalidOrderSize: number | undefined;
    const isValidSize =
      orderType === OrderType.SCALE
        ? scaleOrders.every((o) => {
            const orderSize = +o.order.size * +o.order.limitPrice;

            if (orderSize < MIN_SIZE_USD) {
              invalidOrderSize = orderSize;
            }

            return orderSize >= MIN_SIZE_USD;
          })
        : bigIntToNum(
            size * midPrice,
            marketSpec.sizeDecimals + marketSpec.priceDecimals,
          ) >= MIN_SIZE_USD;

    if (orderType === OrderType.SCALE && invalidOrderSize !== undefined) {
      return {
        buttonDisabled: true,
        buttonText: t('orderForm.scaleOrderTooSmall', {
          orderSize: `$${invalidOrderSize.toFixed(2)}`,
          minSize: `$${MIN_SIZE_USD}`,
        }),
      };
    }

    if (!isValidSize) {
      if (!reduceOnly) {
        return {
          buttonDisabled: true,
          buttonText: t('orderForm.minOrderSize', {
            minSize: `$${MIN_SIZE_USD}`,
          }),
        };
      } else {
        const isClosingFullPosition =
          currentPosition &&
          currentPosition.isLong !== isBuy &&
          size === currentPosition.size;

        if (!isClosingFullPosition) {
          return {
            buttonDisabled: true,
            buttonText: t('orderForm.minOrderSize', {
              minSize: `$${MIN_SIZE_USD}`,
            }),
          };
        }
      }
    }

    return { buttonDisabled: false };
  }, [
    t,
    size,
    hasAuth,
    orderType,
    limitPrice,
    startPrice,
    endPrice,
    ordersCount,
    sizeSkew,
    availableFunds,
    isBuy,
    reduceOnly,
    tpPrice,
    slPrice,
    tpslChecked, // Add this to re-validate when TP/SL checkbox is toggled
    isFetchAccountPending,
    midPrice,
    availableFunds,
    isFundedOrEval,
    capitalStatus,
    scaleOrders,
    timeInForce,
    currentPosition,
    bestAsk,
    bestBid,
    bboPrice,
  ]);

  const { isSubmitting } = form.formState;

  const button = (
    <Button
      type="submit"
      variant={isBuy ? 'default' : 'destructive'}
      className="h-16 w-full rounded-none font-mono text-lg font-normal uppercase tracking-wider"
      size="lg"
      disabled={buttonDisabled || isWeekendBlocked || isMigrationBlocked}
      loading={isSubmitting}
    >
      {isMigrationBlocked
        ? t(getMigrationBlockButtonKey(migrationReason))
        : isWeekendBlocked
          ? t('orderForm.weekendPaused')
          : buttonText ?? t('orderForm.placeOrder')}
    </Button>
  );

  if (isMigrationBlocked) {
    return (
      <div className="w-full">
        <TooltipWrapper
          content={t(getMigrationBlockTooltipKey(migrationReason))}
        >
          {button}
        </TooltipWrapper>
      </div>
    );
  }

  if (isWeekendBlocked) {
    return (
      <div className="w-full">
        <TooltipWrapper
          content={t('marketInfo.marketOpensInTooltip', {
            reopen: reopenLabel,
          })}
        >
          {button}
        </TooltipWrapper>
      </div>
    );
  }

  return button;
}
